import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  Circle,
  useMap,
} from "react-leaflet";
import { Card, Row, Col, Tooltip, Space, Button } from "antd";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Car from "../../../img/asset/car.png";
import "leaflet-rotatedmarker";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";
import useTable from "../../hooks/table";
import useDrawer from "../../hooks/drawer";
import CustomFunctions from "../../dependencies/custom/custom-functions";

const FitBounds = ({ path }) => {
  const map = useMap();
  if (path && path.length > 0) {
    map.fitBounds(path);
  }
  return null;
};

export default function TripHistory() {
  const navigate = useNavigate();
  const drawer = useDrawer();
  const gpsDrawer = useDrawer();
  const [path, setPath] = useState([]);
  const table = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  const listTable = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  const columns = [
    {
      title: `Driver's Name`,
      dataIndex: "driverName",
      ...table?.getColumnSearchProps("driverName"),
    },
    {
      title: "Bus",
      dataIndex: "busPlate",
      ...table?.getColumnSearchProps("busPlate"),
    },
    {
      title: "Route",
      dataIndex: "routeName",
      ...table?.getColumnSearchProps("routeName"),
    },
    {
      title: "Trip Type",
      dataIndex: "tripType",
      ...table?.getColumnSearchProps("tripType"),
    },
    {
      title: "Duration",
      dataIndex: "timeTaken",
    },
    {
      title: "Status",
      dataIndex: "status",
      ...table?.getColumnSearchProps("status"),
    },
    {
      title: "Date",
      dataIndex: "date",
      ...table?.getColumnSearchProps("date"),
    },
    {
      title: "Action",
      key: "action",
      render: (v, record) => (
        <Space size="middle">
          <Tooltip title="Show GPS coordinates on the map">
            <Button
              className="btn-info border-0"
              onClick={(e) => getData(v, "gps_data", "gps")}
              icon={<i className="fas fas fa-location-arrow" />}
            >
              GPS
            </Button>
          </Tooltip>
          <Tooltip title="Get the list of students who were on this trip">
            <Button
              className="btn-success border-0"
              onClick={(e) => getData(v, "actual_list")}
              icon={<i className="fas fa-users" />}
            >
              Onboarded
            </Button>
          </Tooltip>
          <Tooltip title="Get the list of students who were supposed to be on this trip">
            <Button
              className="btn-secondary border-0"
              onClick={(e) => getData(v, "assumed_list")}
              icon={<i className="fas fa-users" />}
            >
              Assumed
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const studentColumns = [
    {
      title: "First Name",
      dataIndex: "firstName",
      ...table?.getColumnSearchProps("firstName"),
    },
    {
      title: "Middle Name",
      dataIndex: "middleName",
      ...table?.getColumnSearchProps("middleName"),
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      ...table?.getColumnSearchProps("lastName"),
    },
    {
      title: "Sex",
      dataIndex: "gender",
      ...table?.getColumnSearchProps("gender"),
    },
    {
      title: "Class",
      dataIndex: "className",
      ...table?.getColumnSearchProps("className"),
    },
    {
      title: "Stream",
      dataIndex: "streamName",
      ...table?.getColumnSearchProps("streamName"),
    },
  ];

  useEffect(() => {
    table.setColumns(columns);
    listTable.setColumns(studentColumns);
  }, []);

  useEffect(() => {
    getTripHistory();
  }, []);

  async function getTripHistory() {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings.urls.transport.trip_history,
      undefined,
      navigate,
    );
    if (res?.ok) {
      table.setData(res?.data);
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  }

  async function getData(value, endpoint, type = "table") {
    table.setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings.urls.transport.trip_history_info}/${value?.id}/${endpoint}`,
      undefined,
      navigate,
    );

    if (res?.ok) {
      if (type == "table") {
        const caption =
          endpoint == "actual_list"
            ? "Onboarded Students List"
            : "Assumed Students List";
        listTable.setData(
          res?.data?.onboardedStudents || res?.data?.assumedStudents,
        );
        drawer.setOpen(true);
        drawer.setPlacement("right");
        drawer.setWidth("900");
        drawer.setTitle(
          `${caption} | Driver: ${value?.driverName} | Bus: ${value?.busPlate} | Route: ${value?.routeName}`,
        );
      } else {
        //then it's gps
        let array = [];
        for (let row of res?.data?.gpsData) {
          array.push([parseFloat(row?.latitude), parseFloat(row?.longitude)]);
        }
        const smoothedPath = smoothPath(array);
        // console.log(smoothedPath)
        setPath(smoothedPath);
        gpsDrawer.setOpen(true);
        gpsDrawer.setPlacement("right");
        gpsDrawer.setWidth("100%");
        gpsDrawer.setTitle(
          `Driver: ${value?.driverName} | Bus: ${value?.busPlate} | Route: ${value?.routeName}`,
        );
      }
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
    table.setLoading(false);
  }

  function smoothPath(path = [], windowSize = 3) {
    return path.map((point, i) => {
      const slice = path.slice(
        Math.max(0, i - windowSize),
        Math.min(path.length, i + windowSize + 1),
      );
      const valid = slice.filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng));
      if (valid.length === 0) return point; // fallback
      const lat = valid.reduce((sum, p) => sum + p[0], 0) / valid.length;
      const lng = valid.reduce((sum, p) => sum + p[1], 0) / valid.length;
      return [lat, lng];
    });
  }

  return (
    <>
      <div className="">
        <Row gutter={[16, 0]}>
          <Col xs={24} xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace p-2"
              title={"Trips"}
            >
              <div className="table-responsivex">
                {table.tableWithHeaderFooter(
                  CustomFunctions.tblCaption,
                  CustomFunctions.tblCaption,
                  { scroll: { x: "max-content" } },
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {drawer.drawerJSX(
        1000,
        <>
          {listTable.tableWithHeaderFooter(
            CustomFunctions.tblCaption,
            CustomFunctions.tblCaption,
            { scroll: { x: "max-content" } },
          )}
        </>,
      )}

      {gpsDrawer.drawerJSX(
        1000,
        <>
          <MapContainer zoom={14} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution="© OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Polyline positions={path} color="blue" />
            <FitBounds path={path} />
          </MapContainer>
        </>,
      )}
    </>
  );
}
